import { supabase } from '@/integrations/supabase/client';
import { getDeviceId } from '@/lib/deviceId';

/**
 * Backup channel for why a device landed back on /auth. The URL is the primary
 * one (see authUrlWithNotice); this covers the case where the query string is
 * stripped before the sign-in page reads it.
 */
export const SESSION_NOTICE_KEY = 'bige_session_notice';

export type SessionNotice = {
  /** Held the session until another device signed in and took it over. */
  type: 'evicted';
};

export function setSessionNotice(notice: SessionNotice) {
  try {
    localStorage.setItem(SESSION_NOTICE_KEY, JSON.stringify(notice));
  } catch {
    // Storage blocked — the URL still carries the notice.
  }
}

/**
 * The /auth URL that explains itself. Carrying the reason in the query string
 * means it survives the full page load, needs no async read, and is visible in
 * the address bar when something misbehaves.
 */
export function authUrlWithNotice(notice: SessionNotice): string {
  return `/auth?session=${notice.type}`;
}

/** Reads the notice from the URL, falling back to localStorage. Clears the backup. */
export function readSessionNotice(search: string): SessionNotice | null {
  let stored: SessionNotice | null = null;
  try {
    const raw = localStorage.getItem(SESSION_NOTICE_KEY);
    if (raw) {
      localStorage.removeItem(SESSION_NOTICE_KEY);
      if (JSON.parse(raw)?.type === 'evicted') stored = { type: 'evicted' };
    }
  } catch {
    // Ignore — the query string below is the primary source.
  }

  try {
    if (new URLSearchParams(search).get('session') === 'evicted') return { type: 'evicted' };
  } catch {
    // Fall through to the stored copy.
  }

  return stored;
}

/**
 * Deletes Supabase's stored session directly.
 *
 * `signOut()` is not reliable for an evicted device: the takeover already
 * revoked this session server-side, so GoTrue answers the logout with 403 and
 * the session can survive in localStorage. The device then arrives at /auth
 * still signed in, gets redirected back into the app, and re-claims the account
 * — the two browsers trade the session back and forth. Removing the key
 * ourselves is what actually ends the session here.
 *
 * Returns how many keys were removed.
 */
export function purgeStoredSession(): number {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /^sb-.*-auth-token$/.test(key)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
    return keys.length;
  } catch {
    return 0;
  }
}

/** Frees this device's claim on explicit sign-out, so no stale row is left behind. */
export async function releaseSessionClaim() {
  try {
    await supabase.rpc('release_active_session', { p_device_id: getDeviceId() });
  } catch (err) {
    console.warn('[SingleSession] release failed:', err);
  }
}
